import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./pd-chat-item.js";
import "./pd-chat-input.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { repeat } from "lit/directives/repeat.js";
import { formatRelativeDate } from "@progressive-development/pd-utils";
//#region src/pd-chat/PdChatList.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
/** Abstand, bis zu dem aufeinanderfolgende Nachrichten desselben Autors
*  zu einem Block zusammengefasst werden. */
var GROUP_WINDOW_MS = 3e5;
/** Abstand zum unteren Rand, bis zu dem „unten" gilt. */
var STICK_THRESHOLD_PX = 32;
/** Lokaler Tagesschlüssel — Tageswechsel nach lokaler Zeit, damit
*  „gestern Abend" auch dann gestern ist, wenn keine 24 h vergangen sind. */
var dayKey = (iso) => {
	const d = new Date(iso);
	return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
var PdChatList = class PdChatList extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.entries = [];
		this.fullHeight = false;
		this.loading = false;
		this.inputVariant = "none";
		this.inputDisabled = false;
		this._stick = true;
		this._programmaticScroll = false;
	}
	static {
		this.styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: var(--pd-chat-height, auto);
      max-height: var(--pd-chat-max-height, 60vh);
    }

    /* Modus, nicht Optik: der Schalter gewinnt über die Höhen-Tokens. */
    :host([full-height]) {
      height: auto;
      max-height: none;
    }

    :host([full-height]) .scroll {
      overflow: visible;
    }

    .scroll {
      flex: 1 1 auto;
      /* Ohne min-height: 0 wächst das Flex-Kind über den Container hinaus
         und max-height greift nie. */
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    /* Eigenes Inhaltselement: Es trägt die Flex-Spalte (die Zeilen richten
       sich per align-self daran aus) UND ist das, was der ResizeObserver
       beobachtet — der Scrollcontainer selbst ändert seine Höhe ja nie. */
    .content {
      display: flex;
      flex-direction: column;
    }

    /* Abstände gehören zum Container, nicht zur Zeile — nur er kennt die
       Nachbarschaft; grouped wird dafür am Element reflektiert. */
    .content > pd-chat-item,
    .content > .day {
      margin-top: var(--pd-chat-gap, 0.75rem);
    }

    .content > pd-chat-item[grouped] {
      margin-top: var(--pd-chat-group-gap, 0.15rem);
    }

    .content > *:first-child {
      margin-top: 0;
    }

    /* Die Eingabezeile sitzt UNTER dem Scrollbereich, nicht darin —
       sie scrollt nicht mit und bleibt beim Blättern erreichbar. */
    pd-chat-input {
      flex: none;
      padding-top: var(--pd-chat-gap, 0.75rem);
    }

    .day {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      font-size: var(--pd-chat-meta-font-size, 0.8em);
      color: var(--pd-default-font-muted-col, #757575);
    }

    .day::before,
    .day::after {
      content: "";
      flex: 1;
      border-top: 1px solid
        var(--pd-chat-separator-col, var(--pd-default-light-col, #e0e0e0));
    }

    .empty {
      color: var(--pd-default-font-muted-col, #757575);
      font-style: italic;
      padding: 0.6rem 0;
    }

    /* Ladeanzeige: auf EINE kommende Nachricht wird gewartet, nicht auf
       eine ganze Liste — deshalb Punkte statt Skeleton-Zeilen. */
    .typing {
      align-self: flex-start;
      display: flex;
      gap: 4px;
      padding: 0.7rem 0.85rem;
    }

    .typing span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--pd-chat-typing-col, var(--pd-default-col, #0066cc));
      animation: dotPulse 1.2s ease-in-out infinite;
    }

    .typing span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .typing span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes dotPulse {
      0%,
      80%,
      100% {
        opacity: 0.3;
        transform: scale(0.8);
      }
      40% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .typing span {
        animation: none;
        opacity: 0.6;
      }
    }
  `;
	}
	render() {
		return html`
      <div class="head"><slot name="head"></slot></div>
      <div
        class="scroll"
        part="scroll"
        role="log"
        tabindex="0"
        aria-label="${msg("Nachrichtenverlauf", { id: "pd.chat.list.label" })}"
        @scroll="${this._onScroll}"
      >
        <div class="content">
          ${this._renderRows()} ${this.loading ? this._renderTyping() : nothing}
        </div>
      </div>
      ${this._renderInput()}
      <div class="footer"><slot name="footer"></slot></div>
    `;
	}
	_renderInput() {
		if (this.inputVariant === "none") return nothing;
		return html`
      <pd-chat-input
        variant="${this.inputVariant}"
        .placeHolder="${this.inputPlaceholder}"
        ?disabled="${this.inputDisabled}"
        .submitLabel="${this.submitLabel}"
        .draft="${this.draft}"
        .richInput="${this.richInput}"
      ></pd-chat-input>
    `;
	}
	/**
	* Filter-Hook für den späteren Ausbau — die Renderliste läuft immer
	* durch diesen Schritt (Muster `_visibleEntries` aus pd-mail-list).
	*/
	get _visibleEntries() {
		return this.entries;
	}
	/** Leitet Gruppierung und Tag-Trenner ab. Gibt nur Primitive an die
	*  Zeile weiter — keine neuen Objektidentitäten, die ein Rerendern
	*  der Zeile auslösen würden. */
	_rows() {
		const rows = [];
		let previous;
		for (const entry of this._visibleEntries) {
			const newDay = !previous || dayKey(previous.at) !== dayKey(entry.at);
			const grouped = !!previous && !newDay && previous.userId === entry.userId && Date.parse(entry.at) - Date.parse(previous.at) < GROUP_WINDOW_MS;
			rows.push({
				entry,
				own: !!this.ownUserId && entry.userId === this.ownUserId,
				grouped,
				daySeparator: newDay ? formatRelativeDate(entry.at) : void 0
			});
			previous = entry;
		}
		return rows;
	}
	_renderRows() {
		const rows = this._rows();
		if (rows.length === 0) return this.loading ? nothing : html`
            <div class="empty">
              ${this.emptyText ?? msg("Noch keine Nachrichten", { id: "pd.chat.list.empty" })}
            </div>
          `;
		return repeat(rows, (row) => row.entry.id, (row) => html`
        ${row.daySeparator ? html`<div class="day"><span>${row.daySeparator}</span></div>` : nothing}
        <pd-chat-item
          .entry="${row.entry}"
          ?own="${row.own}"
          ?grouped="${row.grouped}"
        ></pd-chat-item>
      `);
	}
	_renderTyping() {
		return html`
      <div
        class="typing"
        role="status"
        aria-label="${msg("Antwort wird geschrieben", { id: "pd.chat.list.typing" })}"
      >
        <span></span><span></span><span></span>
      </div>
    `;
	}
	/**
	* Merkt sich, ob der Nutzer unten steht.
	*
	* Unser eigenes Scrollen muss hier ausgeklammert werden: Das Event kommt
	* asynchron an, und bis dahin ist der Inhalt oft schon gewachsen (spät
	* gerendertes Markdown). Die Differenz sähe dann wie ein Hochscrollen des
	* Nutzers aus — `_stick` fiele auf false und der Verlauf bliebe für immer
	* über dem unteren Rand stehen.
	*/
	_onScroll() {
		const s = this._scroller;
		if (!s) return;
		if (this._programmaticScroll) {
			this._programmaticScroll = false;
			return;
		}
		this._stick = s.scrollHeight - s.scrollTop - s.clientHeight < STICK_THRESHOLD_PX;
	}
	/**
	* Nur wahr, wenn die Komponente wirklich selbst scrollt. `fullHeight` ist
	* die erklärte Absicht und wird zuerst geprüft — die Messung darunter
	* fängt zusätzlich den Fall ab, dass der Inhalt kürzer ist als der Rahmen.
	*/
	_ownsScroll() {
		if (this.fullHeight) return false;
		const s = this._scroller;
		return !!s && s.scrollHeight > s.clientHeight;
	}
	/**
	* Markdown, Bilder und spät geladene Schriften vergrößern den Inhalt erst
	* NACH `updated()`. Ohne diesen Beobachter bliebe der Verlauf messbar über
	* dem unteren Rand stehen (gemessen: 38 px direkt, 190 px einen Frame
	* später). Beobachtet wird das Inhaltselement — der Scrollcontainer selbst
	* behält ja seine Höhe.
	*/
	firstUpdated() {
		if (!this._content) return;
		this._resizeObserver = new ResizeObserver(() => this._stickToBottom());
		this._resizeObserver.observe(this._content);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this._resizeObserver?.disconnect();
		this._resizeObserver = void 0;
	}
	/** Ans untere Ende, sofern der Nutzer dort steht und die Komponente
	*  überhaupt selbst scrollt. */
	_stickToBottom() {
		if (!this._stick || !this._ownsScroll()) return;
		const s = this._scroller;
		const vorher = s.scrollTop;
		s.scrollTop = s.scrollHeight;
		if (s.scrollTop !== vorher) this._programmaticScroll = true;
	}
	updated(changed) {
		if (changed.has("entries") || changed.has("loading")) this._stickToBottom();
	}
	/**
	* Springt ans Ende des Verlaufs. Wirkt auch bei `fullHeight` — dort
	* scrollt `scrollIntoView` den nächsten scrollbaren Vorfahren, auch über
	* Shadow-Grenzen hinweg. Automatisch geschieht das dort bewusst nicht:
	* ungefragt die Seite zu scrollen wäre übergriffig.
	*/
	scrollToLatest() {
		if (this._ownsScroll()) {
			this._scroller.scrollTop = this._scroller.scrollHeight;
			return;
		}
		this._lastItem?.scrollIntoView({ block: "end" });
	}
};
__decorate([property({ attribute: false })], PdChatList.prototype, "entries", void 0);
__decorate([property()], PdChatList.prototype, "ownUserId", void 0);
__decorate([property({
	type: Boolean,
	reflect: true,
	attribute: "full-height"
})], PdChatList.prototype, "fullHeight", void 0);
__decorate([property({ type: Boolean })], PdChatList.prototype, "loading", void 0);
__decorate([property()], PdChatList.prototype, "emptyText", void 0);
__decorate([property()], PdChatList.prototype, "inputVariant", void 0);
__decorate([property()], PdChatList.prototype, "inputPlaceholder", void 0);
__decorate([property({ type: Boolean })], PdChatList.prototype, "inputDisabled", void 0);
__decorate([property()], PdChatList.prototype, "submitLabel", void 0);
__decorate([property()], PdChatList.prototype, "draft", void 0);
__decorate([property({ attribute: false })], PdChatList.prototype, "richInput", void 0);
__decorate([query(".scroll")], PdChatList.prototype, "_scroller", void 0);
__decorate([query(".content")], PdChatList.prototype, "_content", void 0);
__decorate([query("pd-chat-item:last-of-type")], PdChatList.prototype, "_lastItem", void 0);
PdChatList = __decorate([localized()], PdChatList);
//#endregion
export { PdChatList };
